import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Copy,
    Check,
    ArrowRight,
    BadgeCheck,
    Camera,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import OrganicShapes from "@/components/OrganicShapes";
import { CATEGORY_LIST, CATEGORIES, DONATE_PRESETS } from "@/lib/mockData";
import { SEEDH } from "@/lib/seedh";
import { submitDonation } from "@/lib/api";

const TRUST_POINTS = [
    {
        icon: BadgeCheck,
        title: "Verified records",
        body: "Every contribution gets a unique, publicly searchable ID.",
    },
    {
        icon: Camera,
        title: "Field updates",
        body: "Photos and status notes added by our team after the drive.",
    },
    {
        icon: ShieldCheck,
        title: "Digital proof",
        body: "Downloadable certificates for trees, food, and education.",
    },
];

export default function Donate() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const initialCause = params.get("cause") || "trees";

    const [cause, setCause] = useState(
        CATEGORIES[initialCause] ? initialCause : "trees",
    );
    const [amount, setAmount] = useState(DONATE_PRESETS[cause][0].amount);
    const [copied, setCopied] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        amount: "",
        reference: "",
        message: "",
    });

    useEffect(() => {
        setAmount(DONATE_PRESETS[cause][0].amount);
    }, [cause]);

    const activeCat = CATEGORIES[cause];

    const handleCopy = () => {
        navigator.clipboard?.writeText(SEEDH.upiId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        // form.amount may hold an edited value like "₹2501"; strip non-digits.
        const customAmount = form.amount
            ? Number(String(form.amount).replace(/[^\d.]/g, "")) || null
            : null;
        try {
            await submitDonation({
                donorName: form.name,
                email: form.email,
                phone: form.phone || null,
                category: cause,
                selectedAmount: amount,
                customAmount,
                transactionId: form.reference,
                message: form.message || null,
            });
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 4500);
            setForm({
                name: "",
                email: "",
                phone: "",
                amount: "",
                reference: "",
                message: "",
            });
        } catch {
            setError(
                "Sorry — your submission couldn't be sent. Please try again in a moment.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="donate-page" className="bg-white pt-32 relative overflow-hidden">
            <OrganicShapes variant={cause === "trees" ? "green" : cause === "food" ? "beige" : "blue"} />

            <section className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <SectionHeading
                    eyebrow="Donate"
                    heading={[
                        "Choose a cause,",
                        "leave a traceable mark.",
                    ]}
                    italicIndex={1}
                    description="Pick where your contribution goes. After UPI payment, share the transaction ID below — we'll verify it and map it to a public ID."
                />

                {/* CAUSE PICKER */}
                <SectionReveal delay={0.15}>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {CATEGORY_LIST.map((c) => {
                            const active = cause === c.key;
                            return (
                                <button
                                    key={c.key}
                                    onClick={() => setCause(c.key)}
                                    data-testid={`cause-${c.key}`}
                                    className={`text-left rounded-3xl border p-6 transition-all ${
                                        active
                                            ? `${c.tintBg} ${c.tintBorder} shadow-[0_22px_50px_-28px_rgba(4,88,134,0.28)]`
                                            : "bg-white border-seedh-border hover:bg-seedh-surface-blue/30"
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <span className={`w-12 h-12 rounded-full bg-white border ${c.tintBorder} flex items-center justify-center`}>
                                            <c.icon className={`w-5 h-5 ${c.accentText}`} />
                                        </span>
                                        {active && (
                                            <span className={`pill text-white ${c.accentBg}`}>
                                                <Check className="w-3 h-3" />
                                                Selected
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-display text-2xl text-seedh-text mt-5 leading-tight">
                                        {c.label}
                                    </div>
                                    <div className="text-xs text-seedh-muted mt-2 font-mono">
                                        ID prefix · {c.idPrefix}-####
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </SectionReveal>
            </section>

            {/* CAUSE PRESETS + UPI + FORM */}
            <section
                className="mt-16 relative py-20 md:py-24 overflow-hidden"
                style={{
                    background: [
                        "radial-gradient(ellipse 70% 65% at -8% 108%, rgba(45,122,45,0.30) 0%, transparent 65%)",
                        "radial-gradient(ellipse 65% 60% at 108% -8%, rgba(4,88,134,0.24) 0%, transparent 65%)",
                        "radial-gradient(ellipse 55% 50% at 85% 90%, rgba(168,108,64,0.13) 0%, transparent 60%)",
                        "radial-gradient(ellipse 60% 55% at 15% 10%, rgba(180,220,165,0.22) 0%, transparent 60%)",
                        "#e8ede5",
                    ].join(", "),
                }}
            >
                <OrganicShapes variant="green" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Presets + Form */}
                    <div className="lg:col-span-7">
                        <span className="eyebrow">Step 1 · Choose amount</span>
                        <h2 className="font-display text-3xl md:text-4xl text-seedh-blue mt-5 leading-tight">
                            Suggested options for{" "}
                            <span className="font-display-italic text-seedh-green">
                                {activeCat.label}
                            </span>
                        </h2>

                        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {DONATE_PRESETS[cause].map((p, i) => {
                                const active = amount === p.amount;
                                return (
                                    <button
                                        key={p.label}
                                        onClick={() => setAmount(p.amount)}
                                        data-testid={`preset-${cause}-${i}`}
                                        className={`rounded-2xl px-5 py-4 text-left border transition-all ${
                                            active
                                                ? "bg-seedh-blue text-white border-seedh-blue shadow-[0_8px_24px_-8px_rgba(4,88,134,0.45)]"
                                                : "bg-white text-seedh-text border-seedh-border hover:border-seedh-blue/30 hover:shadow-sm"
                                        }`}
                                    >
                                        <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-70">
                                            {p.label}
                                        </div>
                                        <div className="font-display text-3xl mt-2">
                                            ₹{p.amount.toLocaleString("en-IN")}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-10">
                            <span className="eyebrow">
                                Step 3 · Submit transaction
                            </span>
                            <h3 className="font-display text-2xl md:text-3xl text-seedh-blue mt-5 leading-tight">
                                Donation details
                            </h3>
                        </div>

                        <form
                            onSubmit={handleSubmit}
                            data-testid="donation-form"
                            className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
                        >
                            <FormInput
                                label="Full name"
                                value={form.name}
                                onChange={(v) => setForm({ ...form, name: v })}
                                required
                                testId="form-name"
                            />
                            <FormInput
                                label="Email"
                                type="email"
                                value={form.email}
                                onChange={(v) => setForm({ ...form, email: v })}
                                required
                                testId="form-email"
                            />
                            <FormInput
                                label="Phone"
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                testId="form-phone"
                            />
                            <FormInput
                                label="Amount donated"
                                value={form.amount || `₹${amount}`}
                                onChange={(v) => setForm({ ...form, amount: v })}
                                testId="form-amount"
                            />
                            <FormInput
                                label="Transaction ID"
                                value={form.reference}
                                onChange={(v) =>
                                    setForm({ ...form, reference: v })
                                }
                                placeholder="UPI Ref ID / UTR after payment"
                                required
                                testId="form-reference"
                                full
                            />
                            <FormInput
                                label="Message (optional)"
                                value={form.message}
                                onChange={(v) =>
                                    setForm({ ...form, message: v })
                                }
                                placeholder="A note for the foundation team"
                                textarea
                                testId="form-message"
                                full
                            />

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="form-submit"
                                className="sm:col-span-2 mt-2 btn-primary rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Submitting…" : "Submit for Review"}
                                <ArrowRight className="w-4 h-4" />
                            </button>

                            {submitted && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="sm:col-span-2 rounded-2xl bg-seedh-surface-green border border-seedh-green/20 p-4 text-sm flex items-start gap-3"
                                    data-testid="form-success"
                                >
                                    <Sparkles className="w-5 h-5 text-seedh-green mt-0.5" />
                                    <div>
                                        <div className="font-semibold text-seedh-text">
                                            Thanks — your submission is in.
                                        </div>
                                        <div className="text-seedh-muted text-xs mt-1">
                                            Our team will verify the transaction
                                            and map it to a public record.
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="sm:col-span-2 rounded-2xl bg-white border border-seedh-border p-4 text-sm"
                                    data-testid="form-error"
                                >
                                    <span className="font-semibold text-seedh-text">
                                        {error}
                                    </span>
                                </motion.div>
                            )}
                        </form>
                    </div>

                    {/* UPI CARD */}
                    <SectionReveal delay={0.15} className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28">
                            <span className="eyebrow">
                                Step 2 · UPI quick pay
                            </span>
                            <h3 className="font-display text-2xl md:text-3xl text-seedh-blue mt-5 leading-tight">
                                Scan & pay
                            </h3>

                            <div className="mt-6 rounded-3xl bg-white border border-seedh-border shadow-sm text-seedh-text p-6">
                                <div className="w-48 mx-auto">
                                    <div className="rounded-2xl border border-seedh-border overflow-hidden">
                                        <img
                                            src="/upi-qr.jpg"
                                            alt="UPI QR Code — scan to pay"
                                            className="w-full h-auto object-cover"
                                        />
                                    </div>
                                    <div className="mt-3 flex justify-center">
                                        <span className="pill bg-seedh-green text-white">
                                            Scan to pay
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-7 rounded-xl bg-seedh-surface-blue border border-seedh-border px-4 py-3 flex items-center justify-between">
                                    <div>
                                        <div className="text-[0.65rem] uppercase tracking-[0.22em] text-seedh-brown">
                                            UPI ID
                                        </div>
                                        <div className="font-mono text-sm text-seedh-blue mt-1">
                                            {SEEDH.upiId}
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCopy}
                                        data-testid="upi-copy"
                                        className="w-9 h-9 rounded-full bg-white border border-seedh-border flex items-center justify-center text-seedh-blue hover:bg-seedh-surface-blue transition-colors"
                                    >
                                        {copied ? (
                                            <Check className="w-4 h-4 text-seedh-green" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                                <div className="mt-5 rounded-xl border border-seedh-border p-4 bg-seedh-surface-beige/40">
                                    <div className="text-[0.65rem] uppercase tracking-[0.22em] text-seedh-brown">
                                        Currently sponsoring
                                    </div>
                                    <div className="font-display text-2xl text-seedh-text mt-1">
                                        {activeCat.label} · ₹{amount.toLocaleString("en-IN")}
                                    </div>
                                </div>
                                <p className="mt-5 text-[0.72rem] text-seedh-muted leading-relaxed">
                                    Use any UPI app (Google Pay, PhonePe, Paytm
                                    etc.) to scan or pay to{" "}
                                    <span className="font-mono">{SEEDH.upiId}</span>.
                                    Then submit the transaction ID below.
                                </p>
                            </div>

                            <div className="mt-6 rounded-2xl bg-white border border-seedh-border shadow-sm overflow-hidden">
                                {TRUST_POINTS.map((t, i) => (
                                    <div
                                        key={t.title}
                                        className={`flex items-start gap-4 px-5 py-4 ${i < TRUST_POINTS.length - 1 ? "border-b border-seedh-border" : ""}`}
                                    >
                                        <span className="w-8 h-8 rounded-full bg-seedh-surface-green border border-seedh-border flex items-center justify-center shrink-0 mt-0.5">
                                            <t.icon className="w-3.5 h-3.5 text-seedh-green" />
                                        </span>
                                        <div>
                                            <div className="text-sm font-semibold text-seedh-text">{t.title}</div>
                                            <div className="text-xs text-seedh-muted mt-0.5 leading-relaxed">{t.body}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </SectionReveal>
                </div>
            </section>

            <section className="relative py-16 md:py-20 text-center border-t border-seedh-border bg-white overflow-hidden">
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse 60% 80% at 50% 110%, rgba(74,154,65,0.08) 0%, transparent 70%)",
                    }}
                />
                <SectionReveal>
                    <h3 className="font-display text-2xl md:text-3xl text-seedh-blue tracking-tight">
                        Looking to verify a past contribution?
                    </h3>
                    <p className="mt-3 text-sm text-seedh-muted max-w-md mx-auto leading-relaxed">
                        Every drive gets a permanent public ID. Search it on
                        our Tracker — no login needed, forever public.
                    </p>
                    <Link
                        to="/tracker"
                        className="mt-7 btn-outline rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2"
                    >
                        Open the Tracker
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </SectionReveal>
            </section>
        </main>
    );
}

function FormInput({
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    required,
    full,
    textarea,
    testId,
}) {
    const className = `w-full rounded-xl bg-white border border-seedh-border px-4 py-3 text-sm text-seedh-text placeholder:text-seedh-muted/50 focus:outline-none focus:border-seedh-blue/50 focus:ring-2 focus:ring-seedh-blue/10 transition-colors ${
        textarea ? "min-h-[100px] resize-y" : ""
    }`;
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`}>
            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-seedh-muted">
                {label}
                {required && <span className="ml-1 text-seedh-green">*</span>}
            </span>
            {textarea ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`mt-2 ${className}`}
                    data-testid={testId}
                    required={required}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`mt-2 ${className}`}
                    data-testid={testId}
                    required={required}
                />
            )}
        </label>
    );
}
